import { Button } from '@atgfr8/atg-ui-tailwind';
import { BasicModal } from '@components/Modal/BasicModal';
import { TextArea } from '@components/TextField/TextArea';
import { closeModal, openModal } from '@store/modals/store';
import {
  canDecideRateRequest,
  decideRateRequest,
  formatRateRequestDate,
  getPendingRateRequest,
  getRateRequesterName,
  isMaxRateRequestEnabled,
  isRateRequestSubmitting,
} from '@store/orders/actions/rateRequestActions';
import { orderStore } from '@store/orders/store';
import type { RateRequest } from '@typeDefinitions/rateRequestTypes';
import { formatAmount } from '@utils/utils';
import { createSignal, Show } from 'solid-js';

const buttonClass = 'rounded-[4px] whitespace-nowrap shrink-0';

const denyModalId = (loadId: number) => `deny-rate-request-${loadId}`;

const DenyRateRequestModal = (props: {
  loadId: number;
  request: RateRequest | null;
}) => {
  const [notes, setNotes] = createSignal('');

  const close = () => {
    setNotes('');
    closeModal(denyModalId(props.loadId));
  };

  const onDeny = async () => {
    const request = props.request;
    if (request === null) {
      close();
      return;
    }
    const denied = await decideRateRequest(request, false, notes());
    if (denied) {
      close();
    }
  };

  return (
    <BasicModal
      id={denyModalId(props.loadId)}
      title="Deny Max Rate Increase"
      width="500px"
      footer={false}
      onClose={close}
    >
      <div class="flex flex-col gap-4 pt-2">
        <TextArea
          label="Notes (optional)"
          value={notes()}
          onInput={(e) => setNotes(e.target.value)}
          rows={4}
          maxChar={2000}
        />
        <div class="flex justify-end gap-2">
          <Button
            class="rounded-[4px]"
            variant="outlined"
            size="sm"
            onClick={close}
            dataTestId="deny-rate-request-cancel-button"
          >
            Cancel
          </Button>
          <Button
            class="rounded-[4px]"
            variant="contained"
            color="error"
            size="sm"
            disabled={isRateRequestSubmitting(props.loadId)}
            onClick={() => void onDeny()}
            dataTestId="deny-rate-request-submit-button"
          >
            Deny
          </Button>
        </div>
      </div>
    </BasicModal>
  );
};

export const MaxRateRequestBanner = (props: { loadId: number }) => {
  const pending = () =>
    isMaxRateRequestEnabled() ? getPendingRateRequest(props.loadId) : null;
  const decidable = () => {
    const request = pending();
    return request !== null && canDecideRateRequest(request) ? request : null;
  };

  return (
    <>
      <Show when={decidable()}>
        {(request) => (
          <div
            class="flex items-center justify-between gap-4 mb-2 px-3 py-2 text-sm bg-white border border-orange-500 rounded-[4px]"
            data-testid="max-rate-request-banner"
          >
            <span>
              Max Rate increase to{' '}
              <span class="font-semibold">
                {formatAmount(request().maxRate)}
              </span>{' '}
              requested by{' '}
              <span class="font-semibold">
                {getRateRequesterName(request())}
              </span>{' '}
              on {formatRateRequestDate(request().requestedDate)}
            </span>
            <div class="flex items-center gap-2">
              <Button
                class={buttonClass}
                variant="contained"
                size="sm"
                disabled={
                  orderStore.isReadOnly || isRateRequestSubmitting(props.loadId)
                }
                onClick={() => void decideRateRequest(request(), true)}
                dataTestId="approve-max-rate-increase-button"
              >
                Approve
              </Button>
              <Button
                class={buttonClass}
                variant="outlined"
                color="error"
                size="sm"
                disabled={
                  orderStore.isReadOnly || isRateRequestSubmitting(props.loadId)
                }
                onClick={() => openModal(denyModalId(props.loadId))}
                dataTestId="deny-max-rate-increase-button"
              >
                Deny
              </Button>
            </div>
          </div>
        )}
      </Show>
      <DenyRateRequestModal loadId={props.loadId} request={pending()} />
    </>
  );
};
